import React, { ReactElement, useCallback, useEffect, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { ListChildComponentProps, VariableSizeList as List } from 'react-window';
import { isNumber } from '../../utils';
import { isExpanded } from './CheckBoxTreeUtils';
import { NodeItem, NodeItemProps } from './NodeItem';
import { useCheckBoxTree } from './useCheckBoxTree';
import { useCheckboxTreeKeyboardSupport } from './useCheckboxTreeKeyboardSupport';
import { CheckBoxTreeVirtualizedProps, Node } from './types';
import { NODE_ITEM_HEIGHT, StyledCheckboxTreeContainer } from './StyledCheckboxTreeElements';

type ElementArray = (ReactElement<NodeItemProps, any> | ElementArray)[];

export const CheckBoxTreeVirtualized = ({
  checkChildren = 'none',
  nodes = [],
  onCheck,
  onExpand,
  expanded: expandedItems,
  checked: checkedItems,
  style,
  keepExpanded,
  dataTest = 'checkbox-tree',
  searchTerm,
  className,
  sx
}: CheckBoxTreeVirtualizedProps) => {
  const listRef = useRef<List>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const { listWrapperRef, handleClick, handleBlur, handleFocus, handleKeyDown, focusNode, focusedNode } =
    useCheckboxTreeKeyboardSupport(dataTest);
  const { checked, expanded, handleChecked, handleExpanded } = useCheckBoxTree(
    expandedItems,
    checkedItems,
    nodes,
    onCheck,
    onExpand,
    checkChildren
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    recalculateList();
  }, [nodes, keepExpanded]);

  const recalculateList = (index = 0) => {
    listRef.current?.resetAfterIndex(index);
    listRef.current?.forceUpdate();
  };

  const renderItem = (node: Node, depth = 0, index = 0, parentValue?: string) => {
    const toggleExpanded = (e: React.MouseEvent) => {
      e.stopPropagation();
      handleExpanded(node);
      recalculateList(index);
      focusNode(e);
    };

    let children: ElementArray = [];

    const expand = isExpanded(node, keepExpanded, expanded);
    if (expand && node.children) {
      children = node.children?.map((child) => renderItem(child, depth + 1, index, node.value));
    }

    const toggleChecked = (e: React.ChangeEvent) => {
      handleChecked(node);
      focusNode(e);
    };

    children.unshift(
      <NodeItem
        node={node}
        keepExpanded={keepExpanded}
        expand={expand}
        checked={checked}
        onExpand={toggleExpanded}
        onCheck={toggleChecked}
        depth={depth}
        parentValue={parentValue}
        key={node.value}
        virtualized
        searchTerm={searchTerm}
        focusedNode={focusedNode}
        onClick={focusNode}
      />
    );

    return children;
  };

  const getExpandedItemCount = useCallback(
    (item: Node) => {
      let count = 1;

      if (isExpanded(item, keepExpanded, expanded)) {
        count += item.children?.map(getExpandedItemCount).reduce((total, count) => total + count, 0) ?? 0;
      }

      return count;
    },
    [expanded, keepExpanded]
  );

  const Row = (props: ListChildComponentProps) => {
    const renderedCell = renderItem(nodes[props.index]!, 0, props.index);
    return <div style={props.style}>{renderedCell}</div>;
  };

  const rowHeight = useCallback(
    (index: number) => getExpandedItemCount(nodes[index]!) * NODE_ITEM_HEIGHT,
    [getExpandedItemCount, nodes]
  );

  return (
    <StyledCheckboxTreeContainer
      data-test={dataTest}
      style={style}
      $virtualized
      ref={listWrapperRef}
      tabIndex={0}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseDown={handleClick}
      onKeyDown={handleKeyDown}
      className={className}
      sx={sx}
    >
      <AutoSizer>
        {({ width, height }) => {
          const fixedListHeight = isNumber(height) ? height : 0;
          const fixedListWidth = isNumber(width) ? width : 0;
          return (
            <List
              itemCount={nodes.length}
              itemSize={rowHeight}
              width={fixedListWidth}
              height={fixedListHeight}
              ref={listRef}
              overscanCount={10}
              innerRef={innerRef}
              className="outer-wrapper"
              outerRef={(elementRefOrNull) => {
                if (elementRefOrNull) {
                  // set a negative number to tabIndex to prevent focus on this element and allow custom keyboard handling
                  elementRefOrNull.tabIndex = -1;
                }
              }}
            >
              {Row}
            </List>
          );
        }}
      </AutoSizer>
    </StyledCheckboxTreeContainer>
  );
};
